import { kvGet, kvSet } from './kv';
import {
  GET_CACHE_TTL_MS,
  getCacheKey,
  isAiImageVideoCacheableGet,
  isAiMusicCacheableGet,
  isCutCacheableGet,
  isDatingCacheableGet,
  isDjCacheableGet,
  isGreetingsCacheableGet,
  isMediaCacheableGet,
  isMessengerCacheableGet,
  isPortalCacheableGet,
  isSaasCacheableGet,
  isSoundCacheableGet,
  isTohearCacheableGet,
  isPlatzviewCacheableGet,
  isTradalousCacheableGet,
  isRechtshilfeCacheableGet,
  isCharityCacheableGet,
} from './queuePolicy';

export type CachedGetEntry<T = unknown> = {
  data: T;
  savedAt: number;
  ttlMs: number;
};

function isCachedGetEntry(value: unknown): value is CachedGetEntry {
  if (!value || typeof value !== 'object') return false;
  const v = value as CachedGetEntry;
  return 'data' in v && typeof v.savedAt === 'number';
}

/** Store last-good JSON for an allowlisted GET (no-op if path not cacheable). */
export async function rememberGetCache(
  platform: string,
  path: string,
  isCacheable: (path: string) => boolean,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  if (!isCacheable(path)) return;
  const entry: CachedGetEntry = { data, savedAt: Date.now(), ttlMs };
  await kvSet(platform, getCacheKey(path), entry);
}

/**
 * Recall last-good GET JSON. Returns even when past TTL (stale better than empty offline).
 * Undefined when never cached.
 */
export async function recallGetCache<T = unknown>(
  platform: string,
  path: string,
  isCacheable: (path: string) => boolean,
): Promise<T | undefined> {
  if (!isCacheable(path)) return undefined;
  const row = await kvGet<CachedGetEntry<T> | T>(platform, getCacheKey(path));
  if (row === undefined) return undefined;
  if (isCachedGetEntry(row)) return row.data as T;
  // Legacy / bare value
  return row as T;
}

/** Store last-good JSON for an allowlisted SaaS GET (no-op if path not cacheable). */
export async function rememberSaasGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('saas', path, isSaasCacheableGet, data, ttlMs);
}

/**
 * Recall last-good SaaS GET JSON. Returns even when past TTL (stale better than empty offline).
 * Undefined when never cached.
 */
export async function recallSaasGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('saas', path, isSaasCacheableGet);
}

export async function rememberPortalGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('portal', path, isPortalCacheableGet, data, ttlMs);
}

export async function recallPortalGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('portal', path, isPortalCacheableGet);
}

export async function rememberMessengerGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('messenger', path, isMessengerCacheableGet, data, ttlMs);
}

export async function recallMessengerGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('messenger', path, isMessengerCacheableGet);
}

export async function rememberDatingGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('dating', path, isDatingCacheableGet, data, ttlMs);
}

export async function recallDatingGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('dating', path, isDatingCacheableGet);
}

export async function rememberPlatzviewGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('platzview', path, isPlatzviewCacheableGet, data, ttlMs);
}

export async function recallPlatzviewGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('platzview', path, isPlatzviewCacheableGet);
}

export async function rememberTohearGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('tohear', path, isTohearCacheableGet, data, ttlMs);
}

export async function recallTohearGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('tohear', path, isTohearCacheableGet);
}

export async function rememberGreetingsGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('greetings', path, isGreetingsCacheableGet, data, ttlMs);
}

export async function recallGreetingsGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('greetings', path, isGreetingsCacheableGet);
}

export async function rememberCutGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('cut', path, isCutCacheableGet, data, ttlMs);
}

export async function recallCutGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('cut', path, isCutCacheableGet);
}

export async function rememberSoundGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('sound', path, isSoundCacheableGet, data, ttlMs);
}

export async function recallSoundGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('sound', path, isSoundCacheableGet);
}

export async function rememberDjGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('dj', path, isDjCacheableGet, data, ttlMs);
}

export async function recallDjGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('dj', path, isDjCacheableGet);
}

export async function rememberTradalousGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('tradalous', path, isTradalousCacheableGet, data, ttlMs);
}

export async function recallTradalousGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('tradalous', path, isTradalousCacheableGet);
}

export async function rememberAiMusicGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('ai-music', path, isAiMusicCacheableGet, data, ttlMs);
}

export async function recallAiMusicGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('ai-music', path, isAiMusicCacheableGet);
}

export async function rememberAiImageVideoGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('ai-image-video', path, isAiImageVideoCacheableGet, data, ttlMs);
}

export async function recallAiImageVideoGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('ai-image-video', path, isAiImageVideoCacheableGet);
}

/** Central media library list metadata (`platzona-offline-media` KV). Never `/file` bytes. */
export async function rememberMediaGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('media', path, isMediaCacheableGet, data, ttlMs);
}

export async function recallMediaGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('media', path, isMediaCacheableGet);
}

export async function rememberRechtshilfeGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('rechtshilfe', path, isRechtshilfeCacheableGet, data, ttlMs);
}

export async function recallRechtshilfeGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('rechtshilfe', path, isRechtshilfeCacheableGet);
}

export async function rememberCharityGetCache(
  path: string,
  data: unknown,
  ttlMs = GET_CACHE_TTL_MS,
): Promise<void> {
  return rememberGetCache('charity', path, isCharityCacheableGet, data, ttlMs);
}

export async function recallCharityGetCache<T = unknown>(path: string): Promise<T | undefined> {
  return recallGetCache<T>('charity', path, isCharityCacheableGet);
}
